// src/context/AuthContext.js - Final version with isLoggingOut
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signOut,
  setPersistence, 
  browserSessionPersistence, 
  inMemoryPersistence 
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../utils/firebase';
import { useFirebase } from './FirebaseContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false); // Track logout state
  const { getUserBranches } = useFirebase();

  const login = async (email, password) => {
    try {
      setLoading(true);

      // 👇 Set session persistence (not permanent)
      await setPersistence(auth, browserSessionPersistence);

      console.log('🔥 Starting Firebase Auth login for:', email);

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      const userRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await signOut(auth);
        throw new Error('משתמש לא נמצא במערכת. פנה למנהל המערכת.');
      }

      const userData = userDoc.data();

      if (userData.belongs !== 'זכיין' && userData.belongs !== 'מנהל סניף') {
        await signOut(auth);
        throw new Error('אין לך הרשאות גישה למערכת');
      }

      const branches = await getUserBranches(userData);

      setUser({
        ...userData,
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        branches: branches
      });

      return { success: true };
    } catch (error) {
      console.error('❌ Login error:', error);

      let errorMessage = error.message;
      if (error.code === 'auth/user-not-found') errorMessage = 'משתמש לא נמצא ב-Firebase Authentication';
      else if (error.code === 'auth/wrong-password') errorMessage = 'סיסמה שגויה';
      else if (error.code === 'auth/invalid-email') errorMessage = 'כתובת אימייל לא תקינה';
      else if (error.code === 'auth/too-many-requests') errorMessage = 'יותר מדי ניסיונות התחברות. נסה שוב מאוחר יותר';
      else if (error.code === 'auth/network-request-failed') errorMessage = 'בעיית רשת. בדוק את החיבור לאינטרנט';
      else if (error.code === 'auth/invalid-credential') errorMessage = 'פרטי התחברות שגויים';

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    if (isLoggingOut) {
      console.log('🔄 Logout already in progress, ignoring...');
      return;
    }

    try {
      setIsLoggingOut(true);
      console.log('🚪 Starting logout process...');

      // Clear user state immediately to prevent UI flickering
      setUser(null);

      // 👇 Set in-memory persistence to prevent automatic re-login
      await setPersistence(auth, inMemoryPersistence);
      
      // Sign out from Firebase
      await signOut(auth);
      
      console.log('✅ Firebase signOut completed');
      
      // Clear any browser storage if on web
      if (typeof window !== 'undefined') {
        // Clear localStorage items that might contain auth data
        const authKeys = Object.keys(localStorage).filter(key => 
          key.includes('firebase') || key.includes('auth') || key.includes('user')
        );
        authKeys.forEach(key => localStorage.removeItem(key));
        
        // Clear sessionStorage
        sessionStorage.clear();
        
        console.log('🧹 Browser storage cleared');
      }

      console.log('✅ User logged out + persistence cleared');
    } catch (error) {
      console.error('❌ Error during logout:', error);
      // Even if logout fails, clear the user state
      setUser(null);
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Don't process auth changes during logout
      if (isLoggingOut) {
        console.log('🔄 Ignoring auth state change during logout');
        return;
      }

      console.log('🔥 Auth state changed:', firebaseUser ? 'User logged in' : 'User logged out');

      if (firebaseUser) {
        try {
          const userRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();

            if (userData.belongs === 'זכיין' || userData.belongs === 'מנהל סניף') {
              const branches = await getUserBranches(userData);

              // Only set user if not logging out
              if (!isLoggingOut) {
                setUser({
                  ...userData,
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  branches: branches
                });
              }
            } else {
              await signOut(auth);
              setUser(null);
            }
          } else {
            await signOut(auth);
            setUser(null);
          }
        } catch (error) {
          console.error('❌ Error getting user data:', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [getUserBranches, isLoggingOut]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      loading, 
      isLoggingOut // 👈 Make sure this is exported
    }}>
      {children}
    </AuthContext.Provider>
  );
};